import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/api/client";
import { Section, Badge, Empty, StatCard } from "@/components/ui/kit";
import JourneyStepper from "@/components/JourneyStepper";
import { fmtINR, fmtDate } from "@/utils/format";
import { ArrowLeft, Sparkles, Users } from "lucide-react";

export default function ContributionDetail() {
  const { donationId } = useParams();
  const [j, setJ] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    api.get(`/donations/${donationId}/journey`).then(r => setJ(r.data.data)).catch(e => setErr(e.response?.data?.message));
  }, [donationId]);
  if (err) return <Empty title="Contribution not found" note={err} testid="detail-error" />;
  if (!j) return <div className="text-muted-foreground">Loading…</div>;

  const allocated = Number(j.allocatedAmount);
  const utilized = Number(j.utilizedAmount);
  const amt = Number(j.amount);
  const steps = [
    { id: "received", label: "Received", detail: `Receipt ${j.receiptNumber}`, state: "done" },
    { id: "allocated", label: "Allocated", detail: fmtINR(allocated), state: allocated > 0 ? "done" : "active" },
    { id: "utilized", label: "Utilized", detail: fmtINR(utilized), state: utilized > 0 ? "done" : allocated > 0 ? "active" : "pending" },
    { id: "beneficiary", label: "Beneficiaries", detail: `${j.beneficiaries?.length || 0} reached`, state: (j.beneficiaries?.length || 0) > 0 ? "done" : "pending" },
    { id: "impact", label: "Impact", detail: `${j.impactUpdates?.length || 0} updates`, state: (j.impactUpdates?.length || 0) > 0 ? "done" : "pending" },
  ];

  return (
    <div className="space-y-8" data-testid="contribution-detail">
      <Link to="/donor/contributions" data-testid="back-to-contributions" className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-800 hover:underline"><ArrowLeft className="w-3 h-3" /> Back to my contributions</Link>

      <div className="rounded-3xl border border-emerald-900/15 bg-[#F0FAF5] p-8 lg:p-10">
        <div className="overline">{j.purposeType || "Contribution"}</div>
        <h1 className="mt-1 font-display text-4xl font-extrabold text-emerald-950 tracking-tight">
          {fmtINR(amt)}
        </h1>
        <div className="mt-1 font-display text-lg font-semibold text-emerald-800">{j.purposeName}</div>
        <div className="mt-2 text-sm text-muted-foreground">
          {fmtDate(j.donationDate)} • Payment mode <span className="mono">{j.paymentMode}</span> •{" "}
          {j.donationType === "MATCHED" ? <Badge tone="warm">Matched</Badge> : <Badge tone="brand">Self</Badge>}
        </div>
      </div>

      <Section title="Your journey" kicker="From your account, to real change">
        <JourneyStepper steps={steps} />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
          <StatCard label="Received" value={fmtINR(amt)} sub={j.receiptNumber} testid="det-received" />
          <StatCard label="Allocated" value={fmtINR(allocated)} sub={`Remaining unallocated ${fmtINR(j.remainingUnallocated)}`} tone="accent" testid="det-allocated" />
          <StatCard label="Utilized" value={fmtINR(utilized)} sub={`Balance ${fmtINR(j.remainingBalance)}`} tone="warm" testid="det-utilized" />
          <StatCard label="Beneficiaries" value={j.beneficiaries?.length || 0} sub={j.beneficiaries?.[0]?.name || "—"} tone="coral" testid="det-beneficiaries" />
        </div>
      </Section>

      {j.utilizations?.length > 0 && (
        <Section title="Utilization detail" kicker="Where the money went">
          <div className="rounded-xl border border-emerald-900/10 bg-white p-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-widest text-emerald-800/70 border-b border-emerald-900/10">
                  <th className="py-2 pr-4">Date</th><th className="py-2 pr-4">Purpose</th><th className="py-2 pr-4">Amount</th><th className="py-2 pr-4">Note</th>
                </tr>
              </thead>
              <tbody>
                {j.utilizations.map(u => (
                  <tr key={u.utilizationId} className="border-b border-emerald-900/5">
                    <td className="py-3 pr-4 text-muted-foreground">{fmtDate(u.date)}</td>
                    <td className="py-3 pr-4 font-medium text-emerald-950">{u.purpose}</td>
                    <td className="py-3 pr-4 mono font-semibold text-orange-700">{fmtINR(u.amount)}</td>
                    <td className="py-3 pr-4 text-muted-foreground">{u.description}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {j.beneficiaries?.length > 0 && (
        <Section title="Beneficiaries" kicker={<><Users className="inline w-3 h-3 mr-1" />People your gift reached</>}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {j.beneficiaries.map(b => (
              <div key={b.beneficiaryId} className="rounded-xl border border-emerald-900/10 bg-white p-4">
                <div className="flex items-center justify-between">
                  <div className="mono text-xs text-emerald-900">{b.code}</div>
                  <Badge tone={b.type === "STUDENT" ? "brand" : b.type === "SCHOOL" ? "indigo" : "warm"}>{b.type}</Badge>
                </div>
                <div className="mt-2 font-display font-semibold text-emerald-950">{b.name}</div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {j.impactUpdates?.length > 0 && (
        <Section title="Impact updates" kicker={<><Sparkles className="inline w-3 h-3 mr-1" /> Stories</>}>
          <div className="space-y-3">
            {j.impactUpdates.map(u => (
              <div key={u.id} className="rounded-xl border border-emerald-900/10 bg-white p-5">
                <div className="overline">{fmtDate(u.date)}</div>
                <div className="mt-1 font-display font-bold text-emerald-950">{u.title}</div>
                <p className="mt-1 text-sm text-muted-foreground">{u.description}</p>
              </div>
            ))}
          </div>
        </Section>
      )}
    </div>
  );
}
